import React, { useEffect, useState } from 'react';
import { SearchIcon, Eye, Download, Plus, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardBody, Table } from 'reactstrap';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import MyPagination from '../pagination';
import { useLoader } from '../../_helper/LoaderContext';
import { getTransactionList, paymentPatient } from './service/ledger-service';
import { formatDoctorDisplayDate } from '../../utils/dateTime/dateFormat';
import { useCurrency } from '../../_helper/currencyContext';
import { Link } from 'react-router-dom';
import CurrencyAmountInput1 from '../CurrencyAmountInput1';
import Swal from 'sweetalert2';
import Modal from 'react-bootstrap/Modal';
import { useLab } from '../../_helper/PharmacyProvider';
import { toast } from 'react-toastify';
import { FormLabel } from 'react-bootstrap';
import SearchFilterButtons from '../SearchFilterButtons';

export default function LadgerHome() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const { setLoading } = useLoader();
  const { getCurrencySymbol, convertCurrency, currency } = useCurrency();
  const [ledgerData, setLedgerData] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [show, setShow] = useState(false);
  const [settleLater, setSettleLater] = useState(false);
  const [payment, setPayment] = useState({});
  const [paidAmount, setPaidAmount] = useState("");
  const [total, setTotal] = useState("");
  const { currentLab } = useLab();
  const [currencyFor, setCurrencyFor] = useState("");
  const selectedCurrency = currencyFor || currentLab?.baseCurrency;
  const [outstanding, setOutstanding] = useState({});
  const [data, setData] = useState({});
  const [overpaymentConfirmed, setOverpaymentConfirmed] = useState(false);
  const totalInSelectedCurrency = convertCurrency(
    payment,
    currentLab?.baseCurrency,
    selectedCurrency
  );


  const paidInSelectedCurrency = Number(paidAmount) || 0;
  const remainingInSelectedCurrency = totalInSelectedCurrency - paidInSelectedCurrency;

  useEffect(() => {
    getList()
  }, [currentPage, entriesPerPage])

  const getList = async (query = appliedSearch) => {
    setLoading(true)
    try {
      const form_data = new FormData()
      form_data.append("search", query);
      form_data.append("page", currentPage);
      form_data.append("limit", entriesPerPage);
      const res = await getTransactionList(form_data)
      console.log(res)
      if (res.data.success == true) {
        // setLedgerData(res.data.data)
        // setTotalEntries(res.data.pagination.total || 0);
        setLedgerData(res.data.data.payments)
        setTotalEntries(res.data.data.total || 0);
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const handleSearch = () => {
    const query = search.trim();
    setAppliedSearch(query);
    if (currentPage === 1) {
      getList(query);
    } else {
      setCurrentPage(1);
    }
  };

  const handleClear = () => {
    setSearch("");
    if (!appliedSearch) return;
    setAppliedSearch("");
    if (currentPage === 1) {
      getList("");
    } else {
      setCurrentPage(1);
    }
  };

  const openPayment = (data) => {
    setShow(true);
    setSettleLater(false);
    const rawBalance = data?.balances?.[currentLab?.baseCurrency];
    const balanceNum = typeof rawBalance === 'string' ? Number(rawBalance.replace(/,/g, '')) : Number(rawBalance);
    const safeBalance = !isNaN(balanceNum) && balanceNum >= 0 ? Number(balanceNum.toFixed(2)) : 0;
    setPayment(safeBalance);
    setPaidAmount(safeBalance);
    setTotal(safeBalance.toFixed(2));
    setOutstanding(data?.balances || {});
    setData(data);
  }

  const handleSettleLaterToggle = async () => {
    const willSettle = !settleLater;

    const result = await Swal.fire({
      title: willSettle
        ? t("Are you sure?")
        : t("Are you sure?"),
      text: willSettle
        ? t("Do you want to settle this payment?")
        : t("Do you want to un-settle this payment?"),
      icon: "info",
      showCancelButton: true,
      cancelButtonText: t("No, Cancel"),
      confirmButtonText: willSettle ? t("Yes, Settle it!") : t("Yes, Unsettle it!"),
      reverseButtons: false,
    });

    if (result.isConfirmed) {
      setSettleLater(willSettle);
    }
  };

  const paymentSubmit = async () => {
    let forcePaid = false;

    // Convert total due to the selected currency for accurate comparison
    const totalDueInSelected = totalInSelectedCurrency ?? 0;
    const paidInSelected = Number(
      typeof paidAmount === 'string' ? paidAmount.replace(/,/g, '') : paidAmount
    ) || 0;

    // 🔴 Overpayment check: Compare paid vs due in the SAME (selected) currency
    if (paidInSelected > totalDueInSelected + 0.01) { // small tolerance for floating point
      const extraAmount = paidInSelected - totalDueInSelected;

      const result = await Swal.fire({
        title: t('Overpayment Detected'),
        icon: 'warning',
        html: `
        <p>${t("You are paying")} <strong>${getCurrencySymbol(selectedCurrency)}${paidInSelected.toFixed(2)}</strong></p>
        <p>${t("Outstanding")}: <strong>${getCurrencySymbol(selectedCurrency)}${totalDueInSelected.toFixed(2)}</strong></p>
        <p class="text-danger mt-3 fw-bold">
          ${t("Extra amount")}: ${getCurrencySymbol(selectedCurrency)}${extraAmount.toFixed(2)}
        </p>
      `,
        showCancelButton: true,
        confirmButtonText: t('Yes, Proceed Anyway'),
        cancelButtonText: t('Cancel'),
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return; // User cancelled
      }

      forcePaid = true;
    }

    // 🔵 Now proceed with API call
    setLoading(true);

    try {
      const remaining = Number(remainingInSelectedCurrency?.toFixed(2)) || 0;
      const safePayingAmount = !isNaN(paidInSelected) && paidInSelected >= 0
        ? Number(paidInSelected.toFixed(2))
        : 0;

      // Your ORIGINAL payload — safe numeric payingAmount
      const payload = {
        patientId: data.patientId,
        patientName: data.patientName,
        payingAmount: safePayingAmount,                    // guaranteed number only
        paymentType: paymentMethod,
        currency: currencyFor || currentLab?.baseCurrency,
        isPartial: !forcePaid && remaining > 0 && settleLater === false,
        isSettled: settleLater,
        rePayment: !settleLater,
        customerType: "walking",
      };

      console.log("Payment Payload:", payload);

      const res = await paymentPatient(payload);

      if (res.data.success) {
        toast.success(res.data.message || t("Payment recorded successfully!"));
        setShow(false);
        setCurrencyFor(""); // optional: reset currency
        setSettleLater(false);
        getList();
      } else {
        toast.warning(res.data.message || t("Something went wrong."));
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.warning(t("Payment failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };
  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setEntriesPerPage(newSize);  // Use the correct setter
    setCurrentPage(1);           // Reset to page 1
  }

  const getStartingSerialNumber = () => {
    return (currentPage - 1) * entriesPerPage + 1;
  };

  return (
    <>
      <Card>
        <div className="p-3 mt-4">
          <div className="tw-max-w-full tw-mx-auto">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-4">
              <div className="input-group" style={{ flex: "1 1 260px", maxWidth: 460 }}>
                <span className="input-group-text tw-bg-white" id="basic-addon1"><SearchIcon /></span>
                <input
                  type="text"
                  className="form-control icon_form"
                  placeholder={t("Search By Patient Name")}
                  aria-label={t("Search By Patient Name")}
                  aria-describedby="basic-addon1"
                  style={{ borderLeft: 'none' }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
              <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />
            </div>
            <CardBody className="tw-px-0 py-0">
              <div className="recent-table table-responsive currency-table tw-rounded-lg tw-border tw-border-black custom-slim-scrollbar" style={{ overflowX: "auto" }}>
                <Table className="mb-0 table-center table-bordered table-hover doctor-table" style={{ tableLayout: "fixed", overflowX: 'auto' }}>
                  <thead>
                    <tr>
                      <th className="text-center bg-primary tw-text-center" style={{ width: "50px", whiteSpace: "nowrap" }}>
                        <b>#</b>
                      </th>
                      <th className="bg-primary text-white text-left" style={{ width: '175px', whiteSpace: "nowrap" }}><b>{t("Patient Name")}</b></th>
                      {/* <th className="bg-primary text-white text-left" style={{ width: '10%' }}><b>Type</b></th> */}
                      <th className="bg-primary text-white text-left" style={{ width: '110px', whiteSpace: "nowrap" }}><b>{t("Total Amount")}</b></th>
                      <th className="bg-primary text-white" style={{ width: '90px', whiteSpace: "nowrap" }}><b>{t("Paid")}</b></th>
                      {/* <th className="bg-primary text-white" style={{ width: '100px' }}><b>Currency</b></th> */}

                      {/* ⭐ NEW TOTAL COLUMN */}
                      <th className="bg-primary text-white" style={{ width: '150px', whiteSpace: "nowrap" }}><b>{t("Outstanding Balance")}</b></th>

                      <th className="bg-primary text-white" style={{ width: '85px', whiteSpace: "nowrap" }}><b>{t("Status")}</b></th>
                      <th className="bg-primary text-white" style={{ width: '110px', whiteSpace: "nowrap" }}><b>{t("Date")}</b></th>
                      <th className="bg-primary text-white text-center" style={{ width: '90px', whiteSpace: "nowrap" }}><b>{t("Action")}</b></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.length > 0 ? (
                      ledgerData.map((item, index) => {
                        const serialNumber = getStartingSerialNumber() + index;
                        return (
                          <tr key={item.id} className={index % 2 === 0 ? '' : ''}>
                            <td className="text-center font-weight-bold">
                              {serialNumber}
                            </td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <div
                                className="tw-font-medium tw-ps-1"
                              >
                                {item.patientName}
                              </div>

                            </td>
                            <td className='fw-bold'>
                              {item.netTotal > 0 ? (
                                <>
                                  {getCurrencySymbol(item.currency)}{item.netTotal?.toFixed(2)}
                                </>
                              ) : (
                                <>-</>
                              )}
                            </td>
                            <td className='fw-bold text-success'>
                              {getCurrencySymbol(item.currency)}{item.amount?.toFixed(2)}
                            </td>
                            {/* <td>
                              <div className="fw-bold"> {item.currency}</div>
                            </td> */}
                            <td className="fw-bold">
                              {item.currency &&
                                item.balances &&
                                Object.keys(item.balances).length > 0 &&
                                item.balances[item.currency] !== undefined
                                ? `${getCurrencySymbol(item.currency)}${Number(item.balances[item.currency]).toFixed(2)}`
                                : "0"
                              }
                            </td>

                            <td>
                              {item.isSettled ? (
                                <span className="badge bg-success">{t("Settled")}</span>
                              ) : (
                                <>
                                  {item.isPaid && (
                                    <span className="badge bg-primary">{t("Paid")}</span>
                                  )}
                                  {item.isPartialPaid && (
                                    <span className="badge bg-warning text-dark">{t("Partial Paid")}</span>
                                  )}
                                  {!item.isPaid && !item.isSettled && !item.isPartialPaid && (
                                    <span className="badge bg-primary">{t("Payment")}</span>
                                  )}
                                </>
                              )}

                            </td>
                            <td>
                              {formatDoctorDisplayDate(item.createdAt, currentLab) || "-"}
                            </td>

                            <td className="text-center">
                              <div className="d-inline-flex align-items-center justify-content-center">
                                {item.balances.INR > 0.01 && (
                                  <>
                                    <Tooltip id="pay" place="top" content={t("Pay")} />
                                    <button
                                      className='success-action-button me-1'
                                      data-tooltip-id="pay"
                                      onClick={() => openPayment(item)}
                                    >
                                      <i className="fa fa-money" style={{ fontSize: "18px" }}></i>
                                    </button>
                                  </>
                                )}
                                <Tooltip id="invoice" place="top" content={t("View Invoice")} />
                                <Link to={`${process.env.PUBLIC_URL}/view-transaction/${item.patientId}`}>
                                  <button className='success-action-button me-1' data-tooltip-id="invoice">
                                    <i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
                                  </button>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        )
                      }
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          className="text-center py-4 text-muted"
                        >
                          {t("No data available")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
            <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-between gap-2 mt-2 mb-3 pt-1">
              <div className="show-entries d-inline-flex align-items-center">
                <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                  {t("Show")}&nbsp;
                </FormLabel>
                <select
                  style={{
                    display: "inline-block",
                    width: "auto",
                    height: "28px",
                    padding: "0.15rem 1.6rem 0.15rem 0.5rem",
                    fontSize: "12px",
                    lineHeight: 1.2,
                    margin: "0 4px"
                  }}
                  value={entriesPerPage}
                  onChange={handlePageSizeChange}
                  className="form-control form-control-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </select>
                <FormLabel className="mb-0 text-muted" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                  {t("entries")}
                </FormLabel>
              </div>
              <div className="d-flex justify-content-center justify-content-sm-end overflow-auto" style={{ maxWidth: "100%" }}>
                <MyPagination totalPages={totalPages} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
              </div>
            </div>
            {/* <div className="mt-3">
              <MyPagination totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}></MyPagination>
            </div> */}
          </div>
        </div>
      </Card>

      <Modal
        show={show}
        onHide={() => {
          setShow(false);
          setSettleLater(false);
        }}
        centered
        scrollable
        dialogClassName="compact-payment-modal"
      >
        <style>{`
          .compact-payment-modal {
            max-width: 440px;
          }
          .compact-payment-modal .modal-content {
            border-radius: 8px;
            overflow: hidden;
          }
          .compact-payment-modal .payment-amount-input .mb-3 {
            margin-bottom: 0 !important;
          }
          .compact-payment-modal .payment-amount-input label {
            font-weight: 600;
            margin-bottom: 0.25rem;
            font-size: 13px;
          }
        `}</style>
        <Modal.Header closeButton style={{ backgroundColor: "#dbffe5", padding: "10px 16px" }}>
          <Modal.Title className="fs-6 fw-bold m-0 d-flex align-items-center">
            <i className="fa fa-money me-2"></i> {t("Process Payment")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          <div className="row g-2">
            {/* Payment Method */}
            <div className="col-12">
              <label className="form-label fw-semibold mb-1" style={{ fontSize: "13px" }}>{t("Payment Method")}</label>
              <select className="form-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="Cash">{t("Cash")}</option>
                <option value="Card">{t("Card")}</option>
                <option value="UPI">{t("UPI")}</option>
              </select>
            </div>

            <div className="col-12">
              <div className="rounded border border-success bg-white" style={{ padding: "8px 12px" }}>
                <div className="fw-bold text-dark mb-1" style={{ fontSize: "13px" }}>{t("Outstanding Balances")}:</div>
                <div className="d-flex flex-column gap-1">
                  {Object.entries(outstanding).map(([currency, value]) => (
                    <div
                      key={currency}
                      className="d-flex justify-content-between align-items-center"
                      style={{ fontSize: "13.5px" }}>
                      <span className="text-secondary">{currency}</span>
                      <span className="fw-bold text-dark">{getCurrencySymbol(currency)}{value?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Currency Amount Input */}
            <div className="col-12 payment-amount-input">
              <CurrencyAmountInput1
                label={t("Paid Amount")}
                amount={paidAmount}
                onAmountChange={(value) => {
                  const num = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
                  setPaidAmount(!isNaN(num) && num >= 0 ? num : 0);
                }}
                onCurrencyChange={(currency) => {
                  setCurrencyFor(currency)
                }}
              />
            </div>
            {remainingInSelectedCurrency?.toFixed(2) > 0 && (
              <div className="col-12">
                <div className="rounded border border-success text-danger d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f8fff9", padding: "8px 12px", fontSize: "13.5px" }}>
                  <span className="fw-semibold">{t("Remaining")}:</span>
                  <span className="fw-bold">{getCurrencySymbol(selectedCurrency)} {Number(remainingInSelectedCurrency?.toFixed(2))}</span>
                </div>
              </div>
            )}

            <div className="col-12 pt-1 mt-1">
              <div className="d-flex align-items-center gap-2 mb-0" style={{ whiteSpace: "nowrap" }}>
                <input
                  className="form-check-input m-0 cursor-pointer"
                  type="checkbox"
                  id="settleLater"
                  checked={settleLater}
                  onChange={handleSettleLaterToggle}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label fw-semibold cursor-pointer user-select-none mb-0"
                  htmlFor="settleLater"
                  style={{ fontSize: "13.5px", whiteSpace: "nowrap" }}
                >
                  {t("Payment Settlement")}
                </label>
                <i
                  className="bi bi-info-circle tw-cursor-pointer tw-text-primary"
                  data-tooltip-id="settle-tooltip"
                  data-tooltip-content={t("Settle the payment and mark as settled")}
                  style={{ fontSize: "14px" }}
                />
                <Tooltip id="settle-tooltip" place="bottom" style={{ zIndex: 9999 }} />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 px-3 d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-primary px-3 py-1" style={{ minWidth: "75px" }} onClick={paymentSubmit}>
            {t("Pay")}
          </button>
          <button type="button" className="btn btn-secondary px-3 py-1" style={{ minWidth: "75px" }} onClick={() => {
            setShow(false)
            setCurrencyFor("")
            setSettleLater(false)
          }}>{t("Cancel")}</button>
        </Modal.Footer>
      </Modal>

    </>
  )
}
